import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

type AlertVariant = "success" | "error" | "warning" | "info";

const ICONS: Record<AlertVariant, React.ReactElement> = {
  success: <CheckCircle2 className="h-6 w-6 text-emerald-400" />,
  error: <X className="h-6 w-6 text-red-400" />,
  warning: <AlertTriangle className="h-6 w-6 text-yellow-400" />,
  info: <Info className="h-6 w-6 text-blue-400" />,
};

const TOAST_AUTO_DISMISS_MS = 4500;
// VP-231: toasts carrying an action button live longer so the user can actually click it
const TOAST_WITH_ACTION_AUTO_DISMISS_MS = 8000;

function variantFromToast(variant?: "default" | "destructive"): AlertVariant {
  return variant === "destructive" ? "error" : "success";
}

interface ToastItemProps {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  variant?: "default" | "destructive";
  open?: boolean;
  onDismiss: (id: string) => void;
}

function ToastItem({ id, title, description, action, variant, open, onDismiss }: ToastItemProps) {
  const alertVariant = variantFromToast(variant);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(
      () => onDismiss(id),
      action ? TOAST_WITH_ACTION_AUTO_DISMISS_MS : TOAST_AUTO_DISMISS_MS
    );
    return () => clearTimeout(timer);
  }, [id, open, onDismiss, action]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key={id}
          initial={{ opacity: 0, scale: 0.95, y: -10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -10 }}
          transition={{ duration: 0.25 }}
          className={cn(
            "relative w-full p-4 rounded-2xl shadow-lg border backdrop-blur-md",
            "bg-white/10 dark:bg-black/20 border-white/20 dark:border-white/10",
            "text-white"
          )}
        >
          <button
            onClick={() => onDismiss(id)}
            className="absolute top-3 right-3 rounded-full p-1 hover:bg-white/20 transition"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">{ICONS[alertVariant]}</div>
            <div className="flex flex-col pr-6">
              {title && <h4 className="text-base font-semibold">{title as string}</h4>}
              {description && <p className="text-sm opacity-80 mt-1">{description as string}</p>}
              {/* VP-231: klik w akcję zamyka toast (capture — zanim odpali się onClick akcji) */}
              {action && (
                <div className="mt-2" onClickCapture={() => onDismiss(id)}>
                  {action}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function Toaster() {
  const { toasts, dismiss } = useToast();

  return (
    <div className="fixed top-4 right-4 z-[10000] flex flex-col gap-3 w-full max-w-sm pointer-events-none">
      {toasts.map(({ id, title, description, action, variant, open }) => (
        <div key={id} className="pointer-events-auto">
          <ToastItem
            id={id}
            title={title}
            description={description}
            action={action}
            variant={variant ?? undefined}
            open={open}
            onDismiss={dismiss}
          />
        </div>
      ))}
    </div>
  );
}
